import { api } from './client'

/**
 * F-072 (TASK-0326, REQ-271-001) — client di `DashboardsController` (`/api/dashboards`): catalogo dei widget,
 * layout personale (con il layout di base del ruolo quando non ne esiste uno) e limiti per ruolo (solo Admin).
 * Il server decide quali widget spettano al ruolo: qui non si replica nessuna regola di permesso.
 */
export type WidgetSize = 's' | 'm' | 'l'
export type WidgetPeriod = '7d' | '30d' | '90d' | 'thisMonth'

export interface WidgetFilters {
  period?: WidgetPeriod
  userId?: number
  teamOf?: number
}

export interface DashboardWidgetItem {
  key: string
  size: WidgetSize
  filters?: WidgetFilters | null
  savedViewId?: number | null
}

export interface DashboardCatalogWidget {
  key: string
  label: string
  description: string
  sizes: WidgetSize[]
  defaultSize: WidgetSize
  allowsFilters: boolean
  needsSavedView: boolean
}

export interface DashboardCatalog {
  maxWidgets: number
  periods: WidgetPeriod[]
  widgets: DashboardCatalogWidget[]
}

export interface DashboardLayoutResponse {
  /** Vero se l'utente non ha salvato nulla: il layout è il base del ruolo. */
  isDefault: boolean
  maxWidgets: number
  widgets: DashboardWidgetItem[]
  /** Voci salvate ma non più ammesse al ruolo (l'Admin ha ristretto i limiti dopo). */
  removedKeys: string[]
}

export interface WidgetPolicy {
  role: string
  allowedWidgets: string[]
  maxWidgets: number
  updatedAt: string | null
}

export interface WidgetPoliciesResponse {
  defaultMaxWidgets: number
  maxWidgetsLimit: number
  catalog: { key: string; label: string }[]
  items: WidgetPolicy[]
}

export const dashboardsApi = {
  catalog: () => api<DashboardCatalog>('/api/dashboards/catalog'),
  mine: () => api<DashboardLayoutResponse>('/api/dashboards/me'),
  save: (widgets: DashboardWidgetItem[]) =>
    api<DashboardLayoutResponse>('/api/dashboards/me', { method: 'PUT', body: JSON.stringify({ widgets }) }),
  resetToBase: () => api<void>('/api/dashboards/me', { method: 'DELETE' }),
  policies: () => api<WidgetPoliciesResponse>('/api/dashboards/widget-policies'),
  savePolicy: (role: string, allowedWidgets: string[], maxWidgets: number) =>
    api<WidgetPoliciesResponse>('/api/dashboards/widget-policies', {
      method: 'PUT',
      body: JSON.stringify({ role, allowedWidgets, maxWidgets }),
    }),
  /** F-096: copia la policy di un ruolo su un altro (Admin). 409 se la destinazione ne ha già una, salvo `overwrite`. */
  clonePolicy: (role: string, targetRole: string, overwrite = false) =>
    api<{ id: number; role: string }>(`/api/dashboards/widget-policies/${encodeURIComponent(role)}/clone`, {
      method: 'POST',
      body: JSON.stringify({ targetRole, overwrite }),
    }),
}

/** Intervallo "YYYY-MM-DD" (inclusivo) per il filtro `period` di un widget; null = predefinito del server. */
export function periodToRange(period: WidgetPeriod | undefined, today = new Date()): { from?: string; to?: string } {
  if (!period) return {}
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const to = iso(today)
  if (period === 'thisMonth') return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to }
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90
  const from = new Date(today)
  from.setDate(from.getDate() - (days - 1))
  return { from: iso(from), to }
}
